import {
  LinxAccountType,
  LinxEnhanceAccountType,
  LinxEntityType,
  LinxNotificationChannel,
  LinxProfileType,
  LinxSteps,
} from '@shared/constants';
import {
  LinXTemplateStep,
  LinxRequestDocumentDetails,
  LinxRequestFinancialAccountType,
  LinxRequestMoveMoneyDetails,
  LinxReviewModalData,
  LinxTemplateCreationPayload,
  LinxTemplatePreDefinedFields,
} from '@shared/models';
import { LINX_ALLOWED_COUNTRY_CODES, linkCountryStepApplies } from '@shared/utils';

const getNotificationParameters = (notificationChannel: LinxNotificationChannel): string[] => {
  switch (notificationChannel) {
    case LinxNotificationChannel.Email:
      return ['email'];
    case LinxNotificationChannel.ALL:
      return ['phoneNumber', 'email'];
    case LinxNotificationChannel.Sms:
    default:
      return ['phoneNumber'];
  }
};

export const getTemplateCreationPayload = (templateData: LinxReviewModalData, templateName?: string): LinxTemplateCreationPayload => {
  const {
    entityModalData,
    expiryDays,
    documentAcceptanceDetails,
    enhanceAccountDetails,
    moveMoneyDetails,
    customerMemo,
    enhanceBeneficiaryDetails,
    identityVerificationDetails,
    financialAccountDetails,
    debitAuthorizationDetails,
  } = templateData;

  const isBusiness = entityModalData?.accountType === LinxAccountType.BUSINESS;

  const selectedSteps: LinXTemplateStep[] = [
    {
      name: LinxSteps.Notification,
      input: {
        preDefined: { channel: entityModalData?.notificationChannel },
        admin: { parameters: getNotificationParameters(entityModalData?.notificationChannel ?? LinxNotificationChannel.Sms) },
      },
    },
  ];

  /** Adding Customer account Step */
  if (entityModalData?.entityType === LinxEntityType.CUSTOMER) {
    if (entityModalData.profileType === LinxProfileType.Existing) {
      selectedSteps.push({
        name: isBusiness ? LinxSteps.LookupBusinessProfile : LinxSteps.LookupIndividualProfile,
        input: {
          admin: {
            parameters: ['customerId'],
          },
        },
      });
    } else {
      selectedSteps.push({ name: isBusiness ? LinxSteps.CreateBusinessAccount : LinxSteps.CreateIndividualAccount });
    }
  }

  /** Adding Counterparty account Step */
  if (entityModalData?.entityType === LinxEntityType.COUNTERPARTY) {
    const isNewCounterparty = entityModalData.profileType === LinxProfileType.New;

    selectedSteps.push({
      name: isNewCounterparty ? LinxSteps.CreateCounterparty : LinxSteps.LookupCounterparty,
      input: {
        user: {
          counterpartyType: entityModalData?.accountType,
        },
      },
    });
  }

  /** Adding Enhance Profile Step */
  if (enhanceAccountDetails) {
    if (entityModalData?.entityType === LinxEntityType.CUSTOMER) {
      // Only emit the enhance step when there are profile fields to update.
      // KOR rejects an enhance step with no input (e.g. a documents-only
      // workflow for an existing customer), so it must be omitted entirely.
      if (enhanceAccountDetails.enhanceProfileDetails) {
        selectedSteps.push({
          name: isBusiness ? LinxSteps.EnhanceBusinessProfile : LinxSteps.EnhanceIndividualProfile,
          input: { user: enhanceAccountDetails.enhanceProfileDetails },
        });
      }
    } else {
      selectedSteps.push({
        name: LinxSteps.EnhanceCounterparty,
        input: {
          user: {
            ...enhanceAccountDetails?.enhanceProfileDetails,
            counterpartyType: entityModalData?.accountType,
          },
        },
      });
    }
  }

  /** Adding Collect ID Documents Step */
  if (enhanceAccountDetails?.documentTypes?.length) {
    const hasOtherDocuments = enhanceAccountDetails.documentTypes.includes('other') && !!enhanceAccountDetails.otherDocuments?.length;
    selectedSteps.push({
      name: LinxSteps.CollectDocuments,
      input: {
        user: {
          documentTypes: enhanceAccountDetails.documentTypes,
          accountType: enhanceAccountDetails.accountType,
          ...(hasOtherDocuments ? { otherDocuments: enhanceAccountDetails.otherDocuments } : {}),
        },
      },
    });
  }

  /** Adding Financial Account Step */
  const financialAccount = financialAccountDetails ?? moveMoneyDetails?.financialAccountDetails;
  if (financialAccount) {
    const { financialAccountType, financialAccountSubType, verifyFinancialAccount } = financialAccount;
    const isNewFa = financialAccountType === LinxRequestFinancialAccountType.NEW;
    const isNewOrExisting = financialAccountType === LinxRequestFinancialAccountType.NEW_OR_EXISTING;

    /**
     * Adding Select Country Step.
     *
     * Must precede the financial account step: LinkFinancialAccountHelper reads countryCode while
     * building the aggregation view, so a select-country ordered after it writes the value too late
     * and the link falls back to USA - a wrong Flinks iframe rather than an error.
     */
    if (linkCountryStepApplies(financialAccountSubType)) {
      selectedSteps.push({
        name: LinxSteps.SelectCountry,
        input: { preDefined: { allowedCountryCodes: LINX_ALLOWED_COUNTRY_CODES } },
      });
    }

    selectedSteps.push({
      name: isNewFa
        ? LinxSteps.LinkOrCreateFinancialAccount
        : isNewOrExisting
          ? LinxSteps.SelectOrCreateFinancialAccount
          : LinxSteps.LookupOrVerifyFinancialAccount,
      input: {
        preDefined: {
          financialAccountSubType: financialAccountSubType === 'CARD' ? 'DEBIT' : financialAccountSubType,
        },
        admin: isNewFa || isNewOrExisting ? {} : { parameters: ['counterPartyFinancialAccountId'] },
        user: verifyFinancialAccount ? { verifyFinancialAccount: true } : {},
      },
    });
  }

  /** Adding Debit Authorization Step */
  if (debitAuthorizationDetails) {
    selectedSteps.push({
      name: LinxSteps.CreateDebitAuthorization,
      input: {
        preDefined: {
          ...debitAuthorizationDetails,
        },
      },
    });
  }

  if (debitAuthorizationDetails) {
    const { debitAuthorizationType, ...adaDetails } = debitAuthorizationDetails;

    const isNewADA = debitAuthorizationType === 'NEW';

    const stepName = isNewADA ? LinxSteps.CreateDebitAuthorization : LinxSteps.LookupOrCreateDebitAuthorization;

    selectedSteps.push({
      name: stepName,
      input: {
        preDefined: { ...adaDetails },
      },
    });
  }

  /** Adding Document Acceptance Step */
  if (documentAcceptanceDetails?.length) {
    const acceptanceDocuments: LinxRequestDocumentDetails[] = [];
    documentAcceptanceDetails.forEach((document) => {
      acceptanceDocuments.push({
        documentGroup: document.documentGroup,
        documentId: document.documentId,
      });
    });
    selectedSteps.push({
      name: LinxSteps.DocumentAcceptance,
      input: { user: { documents: acceptanceDocuments } },
    });
  }

  /** Adding Move Money Step */
  if (moveMoneyDetails) {
    addMoveMoneyStep(moveMoneyDetails, selectedSteps);
  }

  /** Adding Beneficiary Details Step */
  if (enhanceBeneficiaryDetails && isBusiness) {
    selectedSteps.push({
      name: enhanceBeneficiaryDetails.beneficiaryType === 'NEW' ? LinxSteps.CreateBeneficiary : LinxSteps.LookupBeneficiary,
    });

    if (enhanceBeneficiaryDetails.enhanceProfileDetails) {
      selectedSteps.push({
        name: LinxSteps.EnhanceBeneficiary,
        input: {
          user: enhanceBeneficiaryDetails.enhanceProfileDetails,
        },
      });
    }
  }

  /** Adding Beneficiary Collect ID Documents Step */
  if (enhanceBeneficiaryDetails?.documentTypes?.length) {
    const hasOtherDocuments =
      enhanceBeneficiaryDetails.documentTypes.includes('other') && !!enhanceBeneficiaryDetails.otherDocuments?.length;
    selectedSteps.push({
      name: LinxSteps.CollectDocuments,
      input: {
        user: {
          documentTypes: enhanceBeneficiaryDetails.documentTypes,
          accountType: LinxEnhanceAccountType.Benificiary,
          ...(hasOtherDocuments ? { otherDocuments: enhanceBeneficiaryDetails.otherDocuments } : {}),
        },
      },
    });
  }

  /** Adding Identity Verification Step */
  if (identityVerificationDetails) {
    selectedSteps.push({
      name: isBusiness ? LinxSteps.SubmitBusinessVerification : LinxSteps.SubmitIndividualVerification,
      input: {
        preDefined: {
          idvProfile: identityVerificationDetails.id,
        },
      },
    });
  }

  return {
    defaultExpiryDays: expiryDays,
    displayName: entityModalData?.templateName ?? templateName,
    definition: {
      steps: selectedSteps,
    },
    customerMemo: customerMemo,
    region: entityModalData?.region,
  };
};

const addMoveMoneyStep = (moveMoneyDetails: LinxRequestMoveMoneyDetails, selectedSteps: LinXTemplateStep[]) => {
  const isWire = moveMoneyDetails?.selectedMoveHowType === 'wire';
  const isACH = moveMoneyDetails?.selectedMoveHowType === 'ach';
  const wireSettlementPriorityOrUndefined = isWire ? moveMoneyDetails?.moveHowWireDetails?.moveWireType : undefined;

  const transactionData: LinxTemplatePreDefinedFields = {
    transactionType: moveMoneyDetails.selectedMoveItem === 'push' ? 'SEND' : 'REQUEST',
    solution: moveMoneyDetails.selectedMoveHowType,
    priority: isACH ? moveMoneyDetails?.moveHowAchDetails?.moveAchType : wireSettlementPriorityOrUndefined,
    currency: 'USD',
    paymentReasonId: moveMoneyDetails.paymentReasonId,
  };

  if (isWire) {
    transactionData.description = moveMoneyDetails?.wireDetails?.narrative;
    transactionData.memo = moveMoneyDetails?.wireDetails?.memo;
  }

  if (isACH) {
    transactionData.rkorACHIndividualId = moveMoneyDetails?.achDetails?.rkorACHIndividualId;
  }

  // Add scheduler fields if present (only isScheduled and scheduleRecurrenceRule are allowed in template preDefined)
  // Note: scheduleName, scheduleStartDateTime, and timeZone are NOT allowed in template pre-defined inputs
  if (moveMoneyDetails.scheduleDetails) {
    const { recurrenceRule } = moveMoneyDetails.scheduleDetails;
    if (recurrenceRule) {
      transactionData.isScheduled = true;
      transactionData.scheduleRecurrenceRule = recurrenceRule;
    }
  }

  selectedSteps.push({
    name: LinxSteps.MoveMoney,
    input: {
      preDefined: { ...transactionData },
      admin: {
        parameters: ['amount', 'originatorFinancialAccountId'],
      },
    },
  });
};
